// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import L from 'leaflet';
import myw from 'myWorld/base/core';
import 'myWorld/features/featureRepresentation';

myw.HybridLayer = L.Layer.extend({
    options: {
        isStatic: true
    },
    /**
     * @class  Layer that renders features in the myWorld database using two rendering types based on the zoom level.  <br/>
     * Implements L.ILayer so that myw.HybridLayer can be added to a leaflet map directly (using the L.layerControl for example)
     * @param  {MyWorldDatasource}  datasource
     * @param  {layerDefinition}        layerDef
     * @constructs
     */
    initialize(datasource, layerDef) {
        this.datasource = datasource;
        this.layerDef = layerDef;
        this._layerForMode = {};
    },

    /*
     * Implementation of L.ILayer interface
     * adds the layer for the current mode
     */
    onAdd(map) {
        this._map = map;
        this.mode = this._getModeForZoom(this._map.getZoom());
        this._setLayer();

        map.on('zoomend', this._onZoomChange.bind(this));
    },

    /**
     * Gets the mode for the layer based on the zoom level
     * @param  {integer} zoom
     * @return {string}        'lowZoom'/'highZoom'
     * @private
     */
    _getModeForZoom(zoom) {
        return this.layerDef.nativeAppVector.fromScale > zoom ? 'lowZoom' : 'highZoom';
    },

    /*
     * Implementation of L.ILayer interface
     * Removes the current layer from the map
     */
    onRemove(map) {
        this._unsetLayer();
        this._map = null;
    },

    /*
     * Adds the layer for the current mode to the map
     */
    _setLayer() {
        this._layer = this._getLayerForMode(this.mode);
        if (this._clipGeometries) this.setClipGeometry(this._clipGeometries);
        this._map.addLayer(this._layer);
        return this._layer;
    },

    /*
     * Obtains the leaflet layer apropriate for the given mode
     * @param  {string} mode 'master' or 'local'
     * @return {promise<L.ILayer>}  Promise will be rejected if datasource can't log in
     */
    _getLayerForMode(mode) {
        if (!this._layerForMode[mode]) {
            const createLayerMethod =
                mode === 'lowZoom' ? '_createTileLayer' : '_createVectorLayer';
            this._layerForMode[mode] = this.datasource[createLayerMethod](this.layerDef);
        }

        return this._layerForMode[mode];
    },

    /*
     * Removes the current layer from the map
     */
    _unsetLayer() {
        if (!this._layer) return;

        this._map.removeLayer(this._layer);
        this._layer = null;
    },

    /**
     * Called when the zoom level is changed on the map
     * If the layer mode has changed, resets the layer so it can be rendered differently
     * @private
     */
    _onZoomChange(e) {
        if (!this._map) return;
        const newMode = this._getModeForZoom(this._map.getZoom());
        if (this.mode !== newMode && this._layer) {
            this.mode = newMode;
            this._unsetLayer();
            this._setLayer();
            this._layer.redraw();
        }
    },

    /**
     * Set clip geometry on the local layer if the layer supports it
     * @param  {polygonGeometry[]} geometries  GeoJSON geometries to clip by
     * See comments for ClippedTileLayer.setClipGeometry() for more information
     */
    setClipGeometry(geometries) {
        this._clipGeometries = geometries;
        if (this._layer && _.isFunction(this._layer.setClipGeometry)) {
            this._layer.setClipGeometry(geometries);
        }
    },

    /**
     * Sets opacity
     */
    setOpacity(opacity) {
        this._getLayerForMode('lowZoom').setOpacity(opacity);
        this._getLayerForMode('highZoom').setOpacity(opacity);
    }
});

export default myw.HybridLayer;
